import KeywordSpotlight from "@/components/KeywordSpotlight";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import ClusterMap from "@/components/ClusterMap";
import { clusters, categories, clusterByCategory } from "@/data/clusters";
import { useSeo } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

export const Clusters = () => {
  useSeo({
    title: "Manufacturing Clusters · 15 Indian Cities · Asia Sourcing",
    description:
      "15 manufacturing clusters across India: Moradabad, Firozabad, Jaipur, Jodhpur, Saharanpur, Panipat, Noida, Delhi, Bhadohi, Varanasi, Agra, Kashmir, Gurugram, Channapatna, Karur. MOQ, lead time, certifications per cluster.",
    path: "/clusters",
    jsonLd: breadcrumb([
      { name: "Home", path: "/" },
      { name: "Manufacturing Clusters", path: "/clusters" },
    ]),
    jsonLdId: "clusters-jsonld",
  });

  const [filter, setFilter] = useState("All");

  const visible = useMemo(() => {
    if (filter === "All") return clusters;
    const ids = clusterByCategory[filter] || [];
    return clusters.filter((c) => ids.includes(c.id));
  }, [filter]);

  return (
    <div data-testid="clusters-page">
      <header className="page-head wrap">
        <Reveal as="p" className="eyebrow">Craft · Manufacturing clusters</Reveal>
        <WordReveal as="h1" text="Fifteen clusters. Eight with our team on the ground." stagger={60} />
        <Reveal as="p" className="lede" delay={200}>
          The catalogue of where our work is made. Tap a node on the map to read the spec sheet —
          MOQ, lead time, certifications, capability.
        </Reveal>
      </header>

      <section className="clusters" style={{ borderTop: "1px solid var(--line)" }}>
        <div className="wrap grid">
          <Reveal>
            <p className="eyebrow">The map</p>
            <h2>Sourced where the craft actually lives.</h2>
            <p className="lede">
              We don’t pool factories in industrial estates. The map below shows the eight cities
              that specialise in the materials we work with — and the certifications they hold.
            </p>
          </Reveal>
          <Reveal>
            <ClusterMap initialId="moradabad" />
          </Reveal>
        </div>
      </section>

      <section className="catalog wrap" id="capability-index">
        <Reveal as="p" className="eyebrow">Capability index</Reveal>
        <Reveal as="h2" className="display" style={{ fontSize: "var(--t-h1)", margin: "1rem 0 2rem" }}>
          Filter by category.
        </Reveal>
        <div className="filter-bar" data-testid="cluster-filters">
          {["All", ...categories].map((c) => (
            <button
              key={c}
              className={`chip ${filter === c ? "active" : ""}`}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="specimen-grid" data-testid="specimen-grid">
          {visible.map((c) => (
            <Reveal key={c.id} data-testid={`specimen-${c.id}`}>
              <Link
                to={`/clusters/${c.id}`}
                className="specimen"
                style={{ display: "block", textDecoration: "none", color: "inherit" }}
                data-testid={`specimen-link-${c.id}`}
              >
                <span className="ix">{c.material}</span>
                <h3>{c.name}</h3>
                <p>{c.detail.capability}</p>
                <div className="specimen-meta">
                  <span><strong>MOQ</strong>{c.detail.moq}</span>
                  <span><strong>Lead</strong>{c.detail.lead}</span>
                  <span><strong>Certs</strong>{c.detail.certs}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="cta wrap">
        <Reveal as="p" className="eyebrow">Start an enquiry</Reveal>
        <Reveal as="h2" className="display">Tell us the category.<br />We’ll find the cluster.</Reveal>
        <Reveal className="row">
          <Link to="/contact" className="btn btn-fill btn-lg" data-testid="clusters-cta-enquiry">Send an enquiry</Link>
          <Link to="/work" className="btn btn-ghost" data-testid="clusters-cta-work">See the work</Link>
        </Reveal>
      </section>
      <KeywordSpotlight type="clusters" />
    </div>
  );
};

export default Clusters;
